import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandFavicon } from '../components/BrandFavicon';
import { RelationshipBadge, TrendBadge } from '../components/visibility/VisibilityBadges';
import { VisibilityBar } from '../components/visibility/VisibilityBadges';
import { RtlMetric, AiLabel } from '../components/common/RtlMetric';
import { resolveAiMentions } from '../utils/visibility-metrics';
import { getHelpHtml } from '../utils/help/help-content';
import type {
  CompetitorInsight,
  CompetitorIntelligenceRow,
  ProviderCompetitionRow,
  PromptCompetitionRow
} from '../types';

function columnHelp(key: string): string | undefined {
  return getHelpHtml(key) || undefined;
}

export interface CompetitorRankingRow extends CompetitorIntelligenceRow {
  managed: CompetitorInsight | null;
}

export function getCompetitorRankingColumns(options: {
  onEdit: (competitor: CompetitorInsight) => void;
  onDelete: (competitor: CompetitorInsight) => void;
}) {
  return [
    {
      field: 'faviconUrl',
      title: 'لوگو',
      tooltip: columnHelp('tables.logo'),
      width: 70,
      sortable: false,
      cellRenderer: (params: { data: CompetitorRankingRow; rowIndex: number }) => (
        <div className="w-100 d-flex h-100 align-items-center">
          <BrandFavicon
            host={params.data.domain}
            faviconUrl={params.data.faviconUrl}
            name={params.data.name}
            colorIndex={params.rowIndex}
          />
        </div>
      )
    },
    {
      field: 'name',
      type: 'string',
      title: 'برند',
      tooltip: columnHelp('tables.brand'),
      minWidth: 180,
      flex: 1,
      cellRenderer: (params: { value: string; data: CompetitorRankingRow }) => (
        <div className="w-100 d-flex h-100 align-items-center gap-2">
          <span
            className="fs--2 fw-medium text-400"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            #{params.data.rank}
          </span>
          <span className="fw-medium text-1000 text-truncate">{params.value}</span>
        </div>
      )
    },
    {
      field: 'relationship',
      title: 'رابطه',
      tooltip: columnHelp('tables.relationship'),
      width: 100,
      sortable: false,
      cellRenderer: () => (
        <div className="w-100 d-flex h-100 align-items-center">
          <RelationshipBadge relationship="competitor" />
        </div>
      )
    },
    {
      field: 'visibilityScore',
      type: 'number',
      title: 'دیده‌شدن در AI',
      tooltip: columnHelp('metrics.ai_visibility'),
      width: 160,
      cellRenderer: (params: { value: number }) => (
        <div className="w-100 d-flex h-100 align-items-center">
          <VisibilityBar score={params.value} />
        </div>
      )
    },
    {
      field: 'aiMentions',
      type: 'number',
      title: 'منشن در AI',
      tooltip: columnHelp('metrics.ai_mentions'),
      width: 120,
      cellRenderer: (params: { data: CompetitorRankingRow }) => resolveAiMentions(params.data)
    },
    {
      field: 'promptCoverage',
      type: 'number',
      title: 'پوشش پرامپت',
      tooltip: columnHelp('metrics.prompt_coverage'),
      width: 130
    },
    {
      field: 'avgPosition',
      type: 'number',
      title: 'میانگین رتبه AI',
      tooltip: columnHelp('metrics.avg_ai_position'),
      width: 140,
      cellRenderer: (params: { value: number | null }) =>
        params.value != null ? `#${params.value}` : '—'
    },
    {
      field: 'trendChange',
      title: 'ترند',
      tooltip: columnHelp('metrics.trend'),
      width: 100,
      cellRenderer: (params: { value: number | null }) => (
        <div className="w-100 d-flex h-100 align-items-center">
          <TrendBadge changePercentage={params.value} />
        </div>
      )
    },
    {
      field: 'actions',
      title: 'عملیات',
      tooltip: columnHelp('tables.actions'),
      width: 100,
      sortable: false,
      searchable: false,
      noFilter: true,
      pinned: 'left',
      cellClass: 'over-flow',
      cellStyle: { textAlign: 'center', margin: '0px' },
      cellRenderer: (params: { data: CompetitorRankingRow }) => {
        const managed = params.data.managed;
        if (!managed) {
          return <span className="fs--2 text-300">—</span>;
        }
        return (
          <div className="w-100 d-flex h-100 align-items-center justify-content-center">
            <span className="allaho_akbar_zIndex">
              <Dropdown
                onClick={e => e.stopPropagation()}
                align="start"
                className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
              >
                <Dropdown.Toggle
                  className="text-600 btn-reveal"
                  split
                  variant="reveal"
                  size="sm"
                >
                  <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="border py-0"
                  popperConfig={{ strategy: 'fixed' }}
                  renderOnMount
                >
                  <Dropdown.Item
                    onClick={() => options.onEdit(managed)}
                    className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon icon="pen" className="me-1 ms-2" />
                    ویرایش
                  </Dropdown.Item>
                  <Dropdown.Divider className="my-0" />
                  <Dropdown.Item
                    onClick={() => options.onDelete(managed)}
                    className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                    حذف
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </span>
          </div>
        );
      }
    }
  ];
}

export function getPromptCompetitionColumns(competitorNames: string[]) {
  return [
    {
      field: 'promptText',
      type: 'string',
      title: 'پرامپت',
      tooltip: columnHelp('tables.prompt'),
      minWidth: 240,
      flex: 1,
      cellRenderer: (params: { value: string }) => (
        <div className="w-100 h-100 d-flex align-items-center text-truncate">{params.value}</div>
      )
    },
    {
      field: 'ownBrandPosition',
      type: 'number',
      title: 'برند شما',
      tooltip: columnHelp('tables.your_brand'),
      width: 110,
      cellRenderer: (params: { value: number | null }) => (
        <span className="fw-medium text-800">
          {params.value != null ? `#${params.value}` : '—'}
        </span>
      )
    },
    ...competitorNames.map(name => ({
      field: `competitor__${name}`,
      title: name,
      tooltip: columnHelp('tables.position'),
      width: 120,
      sortable: false,
      cellRenderer: (params: { data: PromptCompetitionRow }) => {
        const cell = params.data.competitors.find(c => c.brand === name);
        const beatsYou =
          cell?.position != null &&
          params.data.ownBrandPosition != null &&
          cell.position < params.data.ownBrandPosition;
        return (
          <span className={beatsYou ? 'fw-semibold text-warning' : 'text-600'}>
            {cell?.position != null ? `#${cell.position}` : '—'}
          </span>
        );
      }
    }))
  ];
}

export interface ProviderComparisonBrandRow {
  name: string;
  domain: string | null;
  faviconUrl: string | null;
}

export function getProviderComparisonColumns(providerComparison: ProviderCompetitionRow[]) {
  return [
    {
      field: 'name',
      type: 'string',
      title: 'برند',
      tooltip: columnHelp('tables.brand'),
      minWidth: 200,
      flex: 1,
      cellRenderer: (params: { value: string; data: ProviderComparisonBrandRow; rowIndex: number }) => (
        <div className="w-100 d-flex h-100 align-items-center gap-2">
          <BrandFavicon
            host={params.data.domain}
            faviconUrl={params.data.faviconUrl}
            name={params.value}
            colorIndex={params.rowIndex}
          />
          <span className="fw-medium text-1000 text-truncate">{params.value}</span>
        </div>
      )
    },
    ...providerComparison.map(provider => ({
      field: `provider__${provider.modelId}`,
      title: provider.provider,
      tooltip: columnHelp('sections.competitor_by_model'),
      minWidth: 160,
      sortable: false,
      cellRenderer: (params: { data: ProviderComparisonBrandRow }) => {
        const cell = provider.competitors.find(c => c.brand === params.data.name);
        if (!cell) return <span className="text-300">—</span>;
        return (
          <div>
            <p
              className="mb-0 fw-medium text-800"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {cell.visibilityScore}%
            </p>
            <p className="mb-0 d-flex flex-wrap align-items-center gap-1 fs--2 text-400">
              <RtlMetric
                value={cell.avgPosition != null ? `#${cell.avgPosition}` : '—'}
                label="رتبه"
              />
              <span aria-hidden>·</span>
              <RtlMetric
                value={resolveAiMentions(cell)}
                label={<AiLabel prefix="منشن در" />}
              />
            </p>
          </div>
        );
      }
    }))
  ];
}
